import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Star, CheckCircle, Calendar, MapPin, Clock, AlertCircle, Mail, ChevronDown, ChevronUp, Send, X } from "lucide-react";
import { CcEmailInput } from "@/components/cc-email-input";
import { cn } from "@/lib/utils";

interface ReviewData {
  talentName: string;
  talentUserId: string | null;
  clientName: string;
  clientEmail: string | null;
  clientContactName: string | null;
  venueName: string;
  showDate: string | null;
  eventTime: string | null;
  eventOfferingId: string | null;
  // Task #708 — admin-controlled gate for the post-review client email
  // card. When false, hide the entire Email Event Contact section.
  allowClientEmail?: boolean;
  existingReview: {
    rating: number;
    notes: string | null;
    submittedAt: string;
  } | null;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  bodyRich: string | null;
  scope: string;
}

const DEFAULT_TEMPLATE_KEY = (talentId: string) => `review_default_template_${talentId}`;

/** Substitute basic template variables with client/show info.
 * Trims each value at substitution time so a stray space on a stored
 * contactName / clientName never reaches the rendered review request
 * (e.g. "Hello Joan ," — Task #688). All-whitespace values are treated
 * as empty so the template renders cleanly. */
function applyVariables(text: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce(
    (t, [k, v]) => t.replaceAll(k, (v ?? '').trim()),
    text,
  );
}

function stripHtmlForPreview(html: string): string {
  return html
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Convert plain textarea text to HTML paragraphs for proper email rendering */
function plainTextToHtml(text: string): string {
  return text
    .split(/\n\n+/)
    .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export default function PublicReviewPage() {
  const { workspaceId, talentId, invoiceId } = useParams<{
    workspaceId: string;
    talentId: string;
    invoiceId: string;
  }>();

  const [rating, setRating] = useState<number>(0);
  const [notes, setNotes] = useState("");
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [submitted, setSubmitted] = useState(false);

  // Email follow-up state
  const [emailOpen, setEmailOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const { data, isLoading, error } = useQuery<ReviewData>({
    queryKey: ["/api/public/review", workspaceId, talentId, invoiceId],
    queryFn: async () => {
      const response = await fetch(`/api/public/review/${workspaceId}/${talentId}/${invoiceId}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to load show details");
      }
      return response.json();
    },
    enabled: !!(workspaceId && talentId && invoiceId),
  });

  const { data: templates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/public/review-templates", workspaceId, talentId],
    queryFn: async () => {
      const response = await fetch(`/api/public/review-templates/${workspaceId}/${talentId}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!(workspaceId && talentId) && (submitted || !!data?.existingReview),
  });

  // Load saved default template on mount
  useEffect(() => {
    if (!talentId) return;
    const saved = localStorage.getItem(DEFAULT_TEMPLATE_KEY(talentId));
    if (saved) setSelectedTemplateId(saved);
  }, [talentId]);

  // When template is selected, populate subject+body
  useEffect(() => {
    if (!selectedTemplateId || !templates.length || !data) return;
    const tpl = templates.find(t => t.id === selectedTemplateId);
    if (!tpl) return;

    const contactFirst = data.clientContactName?.split(' ')[0] || data.clientName?.split(' ')[0] || '';
    const vars: Record<string, string> = {
      '{{first_name}}': contactFirst,
      '{{contact_name}}': data.clientContactName || data.clientName || '',
      '{{client_name}}': data.clientName || '',
      '{{account_name}}': data.clientName || '',
      '{{talent_name}}': data.talentName || '',
      '{{venue_name}}': data.venueName || data.clientName || '',
    };

    setEmailSubject(applyVariables(tpl.subject, vars));
    // Always strip HTML — both bodyRich and body can contain HTML tags
    const rawBody = tpl.bodyRich || tpl.body || '';
    const bodyText = stripHtmlForPreview(rawBody);
    setEmailBody(applyVariables(bodyText, vars));
  }, [selectedTemplateId, templates, data]);

  const submitMutation = useMutation({
    mutationFn: async (reviewData: { rating: number; notes: string }) => {
      const response = await fetch(`/api/public/review/${workspaceId}/${talentId}/${invoiceId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reviewData),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to submit review");
      }
      return response.json();
    },
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `/api/public/review/${workspaceId}/${talentId}/${invoiceId}/send-followup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ templateId: selectedTemplateId || null, customSubject: emailSubject, customBody: plainTextToHtml(emailBody), cc: emailCc || undefined }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Failed to send email");
      }
      return response.json();
    },
    onSuccess: () => {
      if (setAsDefault && selectedTemplateId && talentId) {
        localStorage.setItem(DEFAULT_TEMPLATE_KEY(talentId), selectedTemplateId);
      }
      setEmailSent(true);
      setEmailOpen(false);
    },
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    try {
      const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
    } catch { return dateStr; }
  };

  const handleSubmit = () => {
    if (rating === 0) return;
    submitMutation.mutate({ rating, notes });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" data-testid="loader" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Unable to Load Review</h2>
              <p className="text-muted-foreground" data-testid="text-error-message">
                {(error as Error).message || "Something went wrong. Please try again later."}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Show Not Found</h2>
              <p className="text-muted-foreground">This review link may be invalid or expired.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const masterTemplates = templates.filter(t => t.scope === "STANDARD");
  const myTemplates = templates.filter(t => t.scope === "USER");

  // Success / already-submitted screen
  if (data.existingReview || submitted) {
    const review = data.existingReview || { rating, notes, submittedAt: new Date().toISOString() };

    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 p-4 py-10">
        <div className="max-w-lg mx-auto space-y-4">

          {/* Review summary card */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2" data-testid="text-success-title">
                  {submitted ? "Review Submitted!" : "Review Already Submitted"}
                </h2>
                <p className="text-muted-foreground mb-6">Thank you for your feedback on this event.</p>

                <div className="bg-muted/50 rounded-lg p-4 text-left">
                  <div className="mb-3">
                    <span className="text-sm text-muted-foreground">Show:</span>
                    <p className="font-medium" data-testid="text-review-client">{data.clientName}</p>
                  </div>
                  <div className="mb-3">
                    <span className="text-sm text-muted-foreground">Your Rating:</span>
                    <p className="font-bold text-xl text-primary" data-testid="text-review-rating">
                      {review.rating}/10
                    </p>
                  </div>
                  {review.notes && (
                    <div>
                      <span className="text-sm text-muted-foreground">Notes:</span>
                      <p className="text-sm mt-1" data-testid="text-review-notes">{review.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email event contact section — Task #708 admin gate */}
          {data.clientEmail && data.allowClientEmail !== false ? (
            <Card>
              <CardContent className="pt-4 pb-4">
                {emailSent ? (
                  <div className="flex items-center gap-3 text-green-600">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm">Email sent!</p>
                      <p className="text-xs text-muted-foreground">Follow-up sent to {data.clientContactName || data.clientEmail}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Collapsed: tap to expand */}
                    <button
                      className="w-full flex items-center justify-between gap-3 text-left"
                      onClick={() => setEmailOpen(o => !o)}
                      data-testid="button-toggle-email"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">Email Event Contact</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {data.clientContactName || data.clientEmail}
                          </p>
                        </div>
                      </div>
                      {emailOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </button>

                    {emailOpen && (
                      <div className="mt-4 space-y-4 border-t pt-4">

                        {/* Template selector */}
                        {templates.length > 0 && (
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Choose a Template
                            </Label>
                            <Select
                              value={selectedTemplateId}
                              onValueChange={setSelectedTemplateId}
                            >
                              <SelectTrigger data-testid="select-template">
                                <SelectValue placeholder="Select a template…" />
                              </SelectTrigger>
                              <SelectContent>
                                {myTemplates.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">My Templates</div>
                                    {myTemplates.map(t => (
                                      <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                                        {t.name}
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {masterTemplates.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                      {myTemplates.length > 0 ? "Master Templates" : "Templates"}
                                    </div>
                                    {masterTemplates.map(t => (
                                      <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                                        {t.name}
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>

                            {/* Set as default */}
                            {selectedTemplateId && (
                              <div className="flex items-center gap-2 pt-1">
                                <Checkbox
                                  id="set-default"
                                  checked={setAsDefault}
                                  onCheckedChange={v => setSetAsDefault(!!v)}
                                  data-testid="checkbox-set-default"
                                />
                                <label htmlFor="set-default" className="text-xs text-muted-foreground cursor-pointer select-none">
                                  Set as my default template
                                </label>
                              </div>
                            )}
                          </div>
                        )}

                        {/* CC */}
                        <CcEmailInput value={emailCc} onChange={setEmailCc} data-testid="input-followup-cc" />

                        {/* Subject */}
                        <div className="space-y-1.5">
                          <Label htmlFor="email-subject" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Subject
                          </Label>
                          <Input
                            id="email-subject"
                            value={emailSubject}
                            onChange={e => setEmailSubject(e.target.value)}
                            placeholder="Subject…"
                            className="text-sm"
                            data-testid="input-email-subject"
                          />
                        </div>

                        {/* Body */}
                        <div className="space-y-1.5">
                          <Label htmlFor="email-body" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Message
                          </Label>
                          <Textarea
                            id="email-body"
                            value={emailBody}
                            onChange={e => setEmailBody(e.target.value)}
                            placeholder="Write your message…"
                            rows={6}
                            className="text-sm resize-none"
                            data-testid="input-email-body"
                          />
                        </div>

                        {sendEmailMutation.isError && (
                          <p className="text-destructive text-xs" data-testid="text-email-error">
                            {(sendEmailMutation.error as Error).message || "Failed to send email. Please try again."}
                          </p>
                        )}

                        <div className="flex gap-2">
                          <Button
                            onClick={() => sendEmailMutation.mutate()}
                            disabled={!emailSubject.trim() || !emailBody.trim() || sendEmailMutation.isPending}
                            className="flex-1"
                            data-testid="button-send-email"
                          >
                            {sendEmailMutation.isPending ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                            ) : (
                              <><Send className="w-4 h-4 mr-2" />Send Email</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setEmailOpen(false)}
                            data-testid="button-cancel-email"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ) : null}

        </div>
      </div>
    );
  }

  // Rating form
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 p-4 py-12">
      <div className="max-w-lg mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" data-testid="text-page-title">Post-Event Review</h2>
              <p className="text-muted-foreground mt-1">How did the event go? Your feedback helps improve future performances.</p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2 mb-6">
              <div className="font-semibold text-lg" data-testid="text-talent-name">{data.talentName}</div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span data-testid="text-client-name">{data.clientName}</span>
                {data.venueName && data.venueName !== data.clientName && <span>at {data.venueName}</span>}
              </div>
              {data.showDate && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span data-testid="text-show-date">{formatDate(data.showDate)}</span>
                </div>
              )}
              {data.eventTime && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span data-testid="text-show-time">{data.eventTime}</span>
                </div>
              )}
            </div>

            <div className="space-y-3 mb-6">
              <Label className="text-base font-medium">
                How would you rate this event? <span className="text-destructive">*</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Click a number to rate from 1 to 11 (displayed as X/10, with 11/10 for exceptional shows)
              </p>
              <div className="flex flex-wrap gap-2 justify-center py-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setRating(num)}
                    onMouseEnter={() => setHoveredRating(num)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className={cn(
                      "w-10 h-10 rounded-lg border-2 font-bold transition-all text-sm",
                      rating === num
                        ? "bg-primary text-primary-foreground border-primary"
                        : hoveredRating === num
                        ? "bg-primary/20 border-primary/50"
                        : "bg-background border-border hover-elevate"
                    )}
                    data-testid={`button-rating-${num}`}
                  >
                    {num}
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-lg font-semibold text-primary" data-testid="text-selected-rating">
                  {rating}/10
                </p>
              )}
            </div>

            <div className="space-y-2 mb-6">
              <Label htmlFor="notes" className="text-base font-medium">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="How was the venue? Any issues or highlights? Feedback for the team…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                data-testid="input-notes"
              />
            </div>

            <Button
              onClick={handleSubmit}
              disabled={rating === 0 || submitMutation.isPending}
              className="w-full"
              size="lg"
              data-testid="button-submit-review"
            >
              {submitMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</>
              ) : "Submit Review"}
            </Button>

            {submitMutation.isError && (
              <p className="text-destructive text-sm text-center mt-3" data-testid="text-submit-error">
                {(submitMutation.error as Error).message || "Failed to submit review. Please try again."}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
